import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { PdfAnnotationAction } from "@/Documents/Models/UI/PdfAnnotationActions";
import { PdfAnnotationSettings } from "./Annotations/Settings/PdfAnnotationSettings";

import styles from "./PdfAnnotationToolbar.module.scss";

interface PdfAnnotationToolbarProps {
    /** The selected action. */
    activeTool?: PdfAnnotationAction;

    /** On toolbar buttons click. */
    onActionClick?: (action: PdfAnnotationAction) => void

    /** On tool settings selection. */
    onActionSettingsChanged: (styles: Record<string, string>) => void;

    /** Enable edition. */
    enableEdition: boolean;

    /** Display signature annotation. */
    displaySignature: boolean;

    /** Enable signature annotation. */
    enableSignature: boolean;

    /** Enable clear button. */
    enableClearButton: boolean;

    /** Selected file ID of the document. */
    selectedFileId: string;

    /** PdfTron annotation manager. */
    annotationManager: Core.AnnotationManager;

    /** Disable Undo and Redo button. */
    disableUndoRedoButton: boolean;
}

interface ToolbarAction {
    element: JSX.Element,
    action: PdfAnnotationAction,
    title: string,
    isDisabled?: boolean
}

function PdfAnnotationToolbar(props: PdfAnnotationToolbarProps): JSX.Element {
    const { getText } = useTranslation();
    const [positionToolSettings, setPositionToolSettings] = useState<{ left: number, top: number }>(null);
    const [visible, setVisible] = useState<boolean>(false);

    // set tool settings position when pdf action changes
    useEffect(() => {
        if (props.activeTool) {
            setPositionToolSettings({
                left: document.getElementsByClassName(styles.active)[0]?.getBoundingClientRect().left,
                top: document.getElementsByClassName(styles.active)[0]?.getBoundingClientRect().top
            });
            setVisible(true);
        }
    }, [props.activeTool]);

    useEffect(() => {
        props.annotationManager?.addEventListener("annotationChanged", (annotations: Core.Annotations.Annotation[], action, { imported }) => {
            if (imported)
                return;

            setVisible(false);
        });
    }, [props.annotationManager]);

    const firstGroup: ToolbarAction[] = useMemo(() => [
        {
            element: editIcon,
            action: PdfAnnotationAction.Select,
            title: getText("DocumentAnnotation_SelectLabel")
        },
        {
            element: (<span className={styles.annotationText}>T</span>),
            action: PdfAnnotationAction.Text,
            title: getText("DocumentAnnotation_TextLabel")
        },
        {
            element: pencilIcon,
            action: PdfAnnotationAction.Pencil,
            title: getText("DocumentAnnotation_PencilLabel")
        },
        {
            element: highlightIcon,
            action: PdfAnnotationAction.Highlight,
            title: getText("DocumentAnnotation_HighlighterLabel")
        },
        {
            element: rectangleIcon,
            action: PdfAnnotationAction.Rectangle,
            title: getText("DocumentAnnotation_RectangleLabel")
        },
        {
            element: eraseIcon,
            action: PdfAnnotationAction.Eraser,
            title: getText("DocumentAnnotation_EraserLabel")
        },
        {
            element: undoIcon,
            action: PdfAnnotationAction.Undo,
            title: getText("DocumentAnnotation_UndoLabel"),
            isDisabled: props.disableUndoRedoButton
        },
        {
            element: redoIcon,
            action: PdfAnnotationAction.Redo,
            title: getText("DocumentAnnotation_RedoLabel"),
            isDisabled: props.disableUndoRedoButton
        }
    ], [getText, props.disableUndoRedoButton]);

    const secondGroup: ToolbarAction[] = useMemo(
        () => {
            let group: ToolbarAction[] = [
                {
                    element: checkIcon,
                    action: PdfAnnotationAction.Check,
                    title: getText("DocumentAnnotation_CheckmarkStampLabel")
                },
                {
                    element: crossIcon,
                    action: PdfAnnotationAction.Cross,
                    title: getText("DocumentAnnotation_CrossStampLabel")
                },
                {
                    element: (<span>R3</span>),
                    action: PdfAnnotationAction.R3,
                    title: getText("DocumentAnnotation_R3StampLabel")
                },
                {
                    element: (<span>R6</span>),
                    action: PdfAnnotationAction.R6,
                    title: getText("DocumentAnnotation_R6StampLabel")
                },
                {
                    element: (<span>R12</span>),
                    action: PdfAnnotationAction.R12,
                    title: getText("DocumentAnnotation_R12StampLabel")
                },
                {
                    element: (<span>R24</span>),
                    action: PdfAnnotationAction.R24,
                    title: getText("DocumentAnnotation_R24StampLabel")
                }
            ];

            if (props.displaySignature) {
                group.push({
                    element: signatureIcon,
                    action: PdfAnnotationAction.Signature,
                    title: getText("SignatureLabel"),
                    isDisabled: !props.enableSignature
                });
            }

            return group;
        }
        , [getText, props.displaySignature, props.enableSignature]
    );

    function selectToolbarAction(toolbarAction: ToolbarAction): React.MouseEventHandler<HTMLLIElement> {
        if (toolbarAction.isDisabled)
            return;

        props.onActionClick(toolbarAction.action);

        if (toolbarAction.action === props.activeTool)
            setVisible(true);
    }

    return (
        <div className={styles.toolbarContainer}>
            <div className={styles.documentsButton}>
                <a onClick={() => sessionStore.dispatch(LocalOperations.Documents.ReturnToSearch)}>
                    {`<< ${getText("DocumentsLabel")}`}
                </a>
            </div>
            {props.enableEdition && (
                <>
                    {PdfAnnotationToolbarActionGroup(firstGroup, props.activeTool, selectToolbarAction)}
                    {PdfAnnotationToolbarActionGroup(secondGroup, props.activeTool, selectToolbarAction)}
                    <div>
                        <button
                            className="btn btn-default"
                            onClick={() => props.onActionClick(PdfAnnotationAction.Clear)}
                            disabled={!props.enableClearButton}
                        >
                            {getText("DocumentAnnotation_ResetLabel")}
                        </button>
                    </div>
                </>
            )}
            <PdfAnnotationSettings
                toolbarAction={props.activeTool}
                onChange={styles => props.onActionSettingsChanged(styles)}
                position={positionToolSettings}
                isVisible={visible}
            />
        </div>
    );
}

function PdfAnnotationToolbarActionGroup(toolbarActions: ToolbarAction[], activeTool: PdfAnnotationAction, onSelectAction: (action: ToolbarAction) => void): JSX.Element {
    return (
        <ul className={styles.annotationGroup} data-cy="documentAnnotationEditor-toolbar-top">
            {toolbarActions.map((toolbarAction, index) => (
                <li
                    key={`PdfAnnotationToolbar_FirstGroup_${index}`}
                    className={`${(activeTool === toolbarAction.action)
                        ? styles.active
                        : ""}
                    ${toolbarAction.isDisabled
                        ? styles.disabled
                        : styles.enabled}`
                    }
                    onClick={() => onSelectAction(toolbarAction)}
                    title={toolbarAction.title}
                >
                    {toolbarAction.element}
                </li>
            ))}
        </ul>
    );
}

const editIcon = (
    <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2.00513 2.4592L7.49935 18.3314C7.55108 18.4809 7.64875 18.6101 7.77838 18.7008C7.90801 18.7914 8.06297 18.8387 8.22111 18.8359C8.37926 18.8332 8.53248 18.7805 8.65889 18.6855C8.78529 18.5904 8.87841 18.4578 8.92492 18.3066L11.1333 11.1295C11.1692 11.0126 11.2333 10.9063 11.3198 10.8198C11.4063 10.7333 11.5126 10.6692 11.6295 10.6333L18.8066 8.42492C18.9578 8.37841 19.0904 8.28529 19.1855 8.15889C19.2805 8.03248 19.3332 7.87926 19.3359 7.72112C19.3387 7.56298 19.2914 7.40802 19.2008 7.27838C19.1101 7.14875 18.9809 7.05109 18.8314 6.99935L2.9592 1.50513C2.8262 1.45909 2.68292 1.4514 2.54575 1.48295C2.40858 1.5145 2.28306 1.58401 2.18354 1.68354C2.08401 1.78306 2.0145 1.90858 1.98295 2.04575C1.9514 2.18292 1.95909 2.3262 2.00513 2.4592V2.4592Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const pencilIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.5 20.2499H5C4.80109 20.2499 4.61032 20.1709 4.46967 20.0302C4.32902 19.8896 4.25 19.6988 4.25 19.4999V15.3105C4.25 15.2121 4.2694 15.1145 4.30709 15.0235C4.34478 14.9325 4.40003 14.8499 4.46967 14.7802L15.7197 3.53022C15.8603 3.38956 16.0511 3.31055 16.25 3.31055C16.4489 3.31055 16.6397 3.38956 16.7803 3.53022L20.9697 7.71956C21.1103 7.86021 21.1893 8.05097 21.1893 8.24989C21.1893 8.4488 21.1103 8.63956 20.9697 8.78022L9.5 20.2499Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.25 6L18.5 11.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const highlightIcon = (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.77595 9.81955C5.77595 9.81955 14.4595 2.00427 15.762 1.136C16.6304 0.267626 21.4063 3.74117 20.9722 6.34613C20.8828 6.88223 12.2886 16.3322 12.2886 16.3322M5.77595 9.81955L12.2886 16.3322M5.77595 9.81955C5.77595 9.81955 5.77595 10.688 5.77595 11.1221C5.77595 11.822 4.08402 14.2137 3.42722 15.1157C3.28076 15.3169 3.30158 15.5946 3.47753 15.7705L3.83492 16.1279M12.2886 16.3322C12.2886 16.3322 10.9374 16.4915 10.1177 16.7664C9.4807 16.98 7.73603 18.1727 6.99003 18.6937C6.78965 18.8337 6.51746 18.8105 6.34463 18.6376C6.1208 18.4138 6.01896 18.3119 6.01896 18.3119M3.83492 16.1279L1.21085 18.7283C1.11008 18.8282 1.14244 18.9983 1.27285 19.0542L3.91655 20.1872C3.99083 20.2191 4.07697 20.2031 4.13487 20.1467L6.01896 18.3119M3.83492 16.1279L6.01896 18.3119" stroke="white" strokeWidth="2" />
    </svg>
);

const rectangleIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.751 4.5H4.25098C3.83676 4.5 3.50098 4.83579 3.50098 5.25V18.75C3.50098 19.1642 3.83676 19.5 4.25098 19.5H20.751C21.1652 19.5 21.501 19.1642 21.501 18.75V5.25C21.501 4.83579 21.1652 4.5 20.751 4.5Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const eraseIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.08301 9.33276L15.447 15.6967" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20.7497 20.2344H7.25622L3.77918 16.7573C3.6399 16.6181 3.52941 16.4527 3.45402 16.2707C3.37864 16.0887 3.33984 15.8937 3.33984 15.6967C3.33984 15.4997 3.37864 15.3047 3.45402 15.1227C3.52941 14.9407 3.6399 14.7753 3.77918 14.636L14.3858 4.02943C14.6671 3.74812 15.0486 3.59009 15.4464 3.59009C15.8443 3.59009 16.2258 3.74812 16.5071 4.02943L20.7497 8.27207C20.889 8.41136 20.9995 8.57672 21.0749 8.7587C21.1503 8.94069 21.1891 9.13575 21.1891 9.33273C21.1891 9.52971 21.1503 9.72477 21.0749 9.90675C20.9995 10.0887 20.889 10.2541 20.7497 10.3934L10.9088 20.2344" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const undoIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 14.25L3.5 9.75L8 5.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M21.5 18.75C21.5 16.3631 20.5518 14.0739 18.864 12.386C17.1761 10.6982 14.8869 9.75 12.5 9.75H3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const redoIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 14.25L21.5 9.75L17 5.25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.5 18.75C3.5 16.3631 4.44821 14.0739 6.13604 12.386C7.82387 10.6982 10.1131 9.75 12.5 9.75H21.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const checkIcon = (
    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.75 6.75049L10.25 17.25L5 12.0005" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const crossIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1L17 17" stroke="white" strokeWidth="2" />
        <path d="M1 17L17 1" stroke="white" strokeWidth="2" />
    </svg>
);

const signatureIcon = (
    <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.29297 8.24414C3.29297 8.24414 4.31674 4.63618 5.75437 2.88782C6.45315 2.03801 7.06481 1.23698 8.1025 1.448C9.03898 1.63844 9.3886 2.41793 9.77974 3.35062C10.6423 5.40751 10.7861 7.07488 10.7861 9.5213C10.7861 11.1154 10.769 13.2546 10.3548 15.5891C9.9065 18.1159 9.54013 19.7543 8.48587 21.8627C8.00153 22.8312 6.91357 23.7814 6.17266 24.3533C5.72168 24.7013 5.1086 24.714 4.61761 24.425V24.425C4.18951 24.1731 3.90736 23.726 3.88967 23.2296C3.86133 22.4347 3.92347 22.1033 4.12505 21.297C4.65218 19.1887 4.85093 17.8498 6.23358 16.309C7.47953 14.9206 8.13734 13.9286 9.77974 13.1209C10.9298 12.5552 11.4735 12.058 12.4154 12.5552C13.0384 12.8841 13.1833 13.0964 13.4217 13.6351C13.7269 14.3244 13.4217 14.4578 13.4217 15.5891C13.4217 16.4856 13.7359 14.2296 13.9968 13.378C14.215 12.6657 14.1506 12.1338 14.6198 11.5782C14.9861 11.1443 15.2747 10.8629 15.8178 10.8069C16.6343 10.7226 17.0058 11.4273 17.543 12.0924C18.1659 12.8637 18.6081 13.4337 19.5556 13.6351C20.7226 13.8832 22.4309 12.5552 22.4309 12.5552" stroke="#A6B3C5" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M1.6377 13.3965L3.24114 14.9999" stroke="#A6B3C5" strokeLinecap="round" />
        <path d="M1.6377 15L3.24114 13.3966" stroke="#A6B3C5" strokeLinecap="round" />
        <path d="M1.5 18.5H23.5" stroke="#A6B3C5" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export { PdfAnnotationToolbar };
